<template>
  <div id="view-log">

    <a-modal
      cl
      title="Retry Lead"
      :visible="retryModal"
      :closable="false"
    >
      <template slot="footer">
        <a-button @click="hideRetryModal">Cancel</a-button>
        <a-button :disabled="newCountry == null" type="primary" @click="retryRecord">Retry</a-button>
      </template>
      <div>
        Country value the user submitted: <span v-if="originalCountry">{{ originalCountry }}</span><span v-else>No country found in results</span>
      </div>
      <div>
        Switch to this country:
        <a-select show-search id="select-country" @change="changeCountry">
          <a-select-option v-for="view in countryList" :key="view.data.name">{{ `${view.data.name}` }}</a-select-option>
        </a-select>
      </div>
      <div v-if="originalCountry">
        <a-checkbox @change="onAliasCheckboxChange">Add {{ originalCountry }} as alias of this country</a-checkbox>
        <p id="checkbox-explanation">
          When adding {{ originalCountry }} as an alias, any new leads with {{ originalCountry }} for their country will automatically have the chosen country used instead. This can be helpful for typos or cities unique to a country, for example, New York City is unique to the US. However, be careful with city/state/province names. For example, Los Angeles is a city name in multiple countries besides the US and should not be set as an alias for the US. Be extra careful creating an alias when the value is a 2 or 3 character word as it may be an ISO standard code for another country that isn't in the system currently. When in doubt, do not create an alias.
        </p>
      </div>
    </a-modal>

    <div class="title-bar">
      <a-icon type="arrow-left" @click="goBack" />
      <h1>View Country Error</h1>
    </div>

    <div class="main-info" v-if="!loadingData">
      <table>
        <tbody>
          <tr>
            <td class="label">Event Date</td>
            <td class="large-value">{{new Date(data.CreatedAt).toLocaleString()}}</td>
          </tr>
          <tr>
            <td class="label">Leadgen Id</td>
            <td
              class="large-value"
            >{{data.LeadGenId.substring(data.LeadGenId.indexOf("-")+1, data.LeadGenId.lastIndexOf("-"))}}</td>
          </tr>
          <tr>
            <td class="label">Event Status</td>
            <td
              class="large-value"
              :class="{error: data.StatusDate.includes('Err'), ok: data.StatusDate.includes('Processed')}"
            >{{getStatusText(data.StatusDate)}}</td>
          </tr>

          <tr>
            <td></td>
            <td>
              <a-button @click="showRetryModal" id="retry-button" :disabled="!countriesLoaded || !mappingsLoaded || retrying" type="primary" :size="'large'">
                <span v-if="retrying">Resubmitting...</span>
                <span v-else-if="!countriesLoaded || !mappingsLoaded">Loading country lookups...</span>
                <span v-else>Remap Country and Resubmit</span>
                <a-icon v-if="!countriesLoaded || !mappingsLoaded || retrying" type="sync" spin />
              </a-button>
            </td>
          </tr>
          <tr v-if="processor == 'google'">
            <td class="label">Original Event Notification Data</td>
            <td>
              <json-viewer :value="notificationData" copyable :expand-depth="3"></json-viewer>
            </td>
          </tr>
          <tr v-if="processor == 'facebook' && data.FbData != null">
            <td class="label">Data Received from Request to Facebook</td>
            <td>
              <json-viewer :value="fbData" copyable :expand-depth="3"></json-viewer>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <a-spin size="large" v-if="loadingData"></a-spin>
  </div>
</template>

<script>
import axios from "axios";
import { mapState, mapActions } from "vuex";
import ACheckbox from "ant-design-vue/es/checkbox/Checkbox";

// convenience function that just returns an empty {} if the parsing fails
const parseJson = (jsonData) => {
  try {
    return JSON.parse(jsonData);
  } catch (e) {
    console.log("unable to parse", jsonData);
    // don't worry about the error
    return {};
  }
};

export default {

  components: {ACheckbox},
  computed: {
    ...mapState(["user", "apiUrl"]),
    fbData() {
      return parseJson(this.data.FbData);
    },
    notificationData() {
      return parseJson(this.data.LeadRequest);
    },
  },
  data() {
    return {
      loadingVersions: true,
      loadingData: true,
      data: null,
      versions: [],

      countryList: [],
      originalCountry: null,
      newCountry: null,
      saveAlias: false,

      retryModal: false,
      countriesLoaded: false,
      mappingsLoaded: false,

      retrying: false,
      retriesCount: 0,
    };
  },
  methods: {
    ...mapActions(["getLookupTable"]),
    changeVersion(val) {
      this.loadingData = true;

      axios({
        url: `${this.apiUrl}/logs`,
        headers: {
          Authorization: this.user.signInUserSession.idToken.jwtToken,
        },
        params: {
          formId: this.formId,
          leadGenId: val,
          action: "LOG_PER_LEADGEN",
          processor: this.processor,
        },
      })
        .then((rslt) => {
          this.data = rslt.data;
          this.loadingData = false;

          const handlebarRegex = /{{(.+)}}/;

          //For facebook leads
          if(this.processor == 'facebook') {
            //We assume at this point that the Mappings field from the V-type lead always has a 'country' element
            const countryMapping = handlebarRegex.exec((JSON.parse(rslt.data.Mappings)).country);

            //If a handlebar value, need to map the correct field first
            if (countryMapping) {
              let fieldToSearchFor = '';

              if (countryMapping[1] in JSON.parse(rslt.data.FBMappedKeys)) {
                //Means the correct key should be mapped first in FBMappedKeys
                fieldToSearchFor = JSON.parse(rslt.data.FBMappedKeys)[countryMapping[1]];
              } else {
                //Means the key was directly inserted in JsonData, not correlated with FBMappedKeys
                fieldToSearchFor = countryMapping[1];
              }

              console.log(countryMapping);
              console.log(this.fbData.field_data);

              //Finding the field in the results data
              const countryObj = this.fbData.field_data.find((field) => {
                return field.name === fieldToSearchFor;
              });

              if (countryObj) {
                //If values is an array, just get first element.
                if (Array.isArray(countryObj.values)) {
                  this.originalCountry = countryObj.values[0];
                } else {
                  this.originalCountry = countryObj.values;
                }
              }
            }
            //If not a handlebar value, get the country value from the Mappings array right away
            else {
              this.originalCountry = (JSON.parse(rslt.data.Mappings)).country;
            }
          }

          //For google leads
          else if(this.processor == 'google') {
            //We assume at this point that the Mappings field from the V-type lead always has a 'COUNTRY' element
            const countryMapping = handlebarRegex.exec((JSON.parse(rslt.data.Mappings)).COUNTRY);

            if (countryMapping) {
              //Finding the field in the results data
              const countryObj = this.notificationData.user_column_data.find((field) => {
                return field.column_id === countryMapping[1];
              });

              if (countryObj) {
                this.originalCountry = countryObj.string_value;
              }
            }
            //If not a handlebar value, get the country value from the Mappings array right away
            else {
              this.originalCountry = (JSON.parse(rslt.data.Mappings)).COUNTRY;
            }
          }

          this.mappingsLoaded = true;
        })
        .catch((err) => {
          alert("Unable to load form");
          console.log(err);
          this.loadingData = false;
        });
    },
    showRetryModal() {
      this.retryModal = true;
    },
    hideRetryModal() {
      this.retryModal = false;
    },
    async retryRecord() {
      this.retryModal = false;
      this.retrying = true;

      let tries = 0;
      let retries_count_upon_start_of_attempt = this.retriesCount;

      //If save new alias checkbox has been checked.
      if(this.saveAlias) {
        //Save alias first
        await axios({
          method: "post",
          url: `${this.apiUrl}/lookup-data`,
          headers: {
            Authorization: this.user.signInUserSession.idToken.jwtToken,
          },
          params: {
            dataType: 'country',
            dataKey: this.originalCountry,
            dataValue: this.newCountry.data.iso2Code,
            leadGenId: this.leadGenId,
            action: "ADD_LOOKUP_KEY",
          },
        });

        //Once new alias has been saved, retry
        await axios({
          method: "post",
          url: `${this.apiUrl}/leads`,
          headers: {
            Authorization: this.user.signInUserSession.idToken.jwtToken,
          },
          params: {
            formId: this.formId,
            leadGenId: this.leadGenId,
            action: "RETRY_LEADGEN",
          },
        })

      }
      //If NOT saving as a new alias, we need to override the country in the lead.
      else {
        //Retry but with the countryOverride parameter.
        await axios({
          method: "post",
          url: `${this.apiUrl}/leads`,
          headers: {
            Authorization: this.user.signInUserSession.idToken.jwtToken,
          },
          params: {
            formId: this.formId,
            leadGenId: this.leadGenId,
            action: "RETRY_LEADGEN",
            countryOverride: this.newCountry.data.iso2Code,
          },
        });
      }

      console.log("Now fetching retry status...")
      //Fetch status of the specific record that was retried (the Primary Type Record) every 5 seconds.
      const intervalId = setInterval(() => {
        axios({
          url: `${this.apiUrl}/logs`,
          headers: {
            Authorization: this.user.signInUserSession.idToken.jwtToken,
          },
          params: {
            formId: this.formId,
            leadGenId: this.leadGenId,
            action: "LOG_PER_LEADGEN",
          },
        })
          .then((update_rslt) => {
            tries++;
            console.log("Fetch status try # " + tries);
            //If the Retries count is greater than what is currently showing on the screen
            // then count it as having run correctly, the data should be updated for the record.
            if (update_rslt.data.Retries > retries_count_upon_start_of_attempt) {
              console.log(`New Status: ${update_rslt.data.StatusDate}`);
              clearInterval(intervalId);

              // Now what we do next is based on the updated Status of the Primary Lead:
              if(update_rslt.data.StatusDate.includes("Processed")) {
                //Going back to Country Errors page if successful
                alert("Successfully processed lead with remapped country.");
                this.goBack();
              } else if (update_rslt.data.StatusDate.includes("ErrCountry")) {
                //This shouldn't really happen unless the country reconciling algorithms has bugs, but just making sure.
                alert("Unable to process lead with remapped country. Please try again.");
              } else {
                //Other statuses might really happen though.
                alert("Unable to process lead with remapped country. Please try again.");
                console.log("Retry successful, but lead resulted in another Error not related to Country.");
                //So let's atleast change the Status displayed on the page based on the new Error.
                Vue.set(this.data, 'StatusDate', update_rslt.data.StatusDate);
              }

              this.retriesCount++;
              this.retrying = false;
            }

            // If the Retries count is still the same number then wait another 5 seconds and repeat.
            // If it goes for more than 60 seconds (12 tries) then just re-enable the Retry button (and clear the interval).
            else if (tries > 12) {
              clearInterval(intervalId);
              alert("Processsing unsuccessful. Please try again.");
              this.retrying = false;
            }

            //If not met any of these 2 conditions above, just let loop the interval...
          })
          .catch((err) => {
            // If there is still an error the Retry button will be re-enabled.
            clearInterval(intervalId);
            alert("Unable to fetch status of remapping!");
            this.retrying = false;
          });
      }, 5000);


    },
    async getCountries() {
      const country_aliases = await this.getLookupTable('country');

      const result = [];
      const map = new Map();
      //Get unique countries from aliases
      for (const item of country_aliases) {
        if(!map.has(item.data.id)){
          map.set(item.data.id, true);
          result.push(item);
        }
      }

      //Sort based on the country names, not based on lookup keys
      result.sort((a,b) => (a.data.name > b.data.name) ? 1 : ((b.data.name > a.data.name) ? -1 : 0));

      this.countryList = result;
      this.countriesLoaded = true;
    },
    changeCountry(val) {
      this.newCountry = this.countryList.find((c) => c.data.name == val);
      console.log(JSON.stringify(this.newCountry));
    },
    onAliasCheckboxChange(e) {
      console.log(`checked = ${e.target.checked}`);
      this.saveAlias = e.target.checked;
    },
    getStatusText(code) {
      if (code.includes("Processed")) {
        return "Processed";
      } else if (code.includes("ErrFB")) {
        return "Error getting data from Facebook";
      } else if (code.includes("ErrInternal")) {
        return "Error processing data";
      } else if (code.includes("ErrICS")) {
        return "Error sending data to ICS";
      } else if (code.includes("ErrCountry")) {
        return "Error with value for country";
      } else if (code.includes("Test")) {
        return "Test Data";
      }
    },
    goBack() {
      this.$router.go(-1);
    },
  },
  mounted() {
    //Getting the initial passed value of Retries from the previous page to this page's own retriesCount,
    // This page has an own retriesCount, as it might be possible to retry multiple times in this page, if retries are not being successful.
    this.retriesCount = this.retries;

    axios({
      url: `${this.apiUrl}/logs`,
      headers: {
        Authorization: this.user.signInUserSession.idToken.jwtToken,
      },
      params: {
        formId: this.formId,
        leadGenId: this.leadGenId.substr(2),
        action: "LOG_VERSIONS_PER_LEADGEN",
        processor: this.processor,
      },
    })
      .then((rslt) => {
        this.loadingVersions = false;
        this.versions = rslt.data;

        this.changeVersion(this.versions.latest.LeadGenId);
      })
      .catch((err) => {
        alert("Unable to load versions");
      });

    this.getCountries();
  },
  props: ["formId", "leadGenId", "processor", "retries"],
};
</script>

<style lang="scss" scoped>
#view-log {
  display: flex;
  flex-direction: column;
}
.main-info {
  text-align: center;

  table {
    margin: 0 auto 40px;
  }
}
td {
  padding: 15px 10px;
  text-align: left;
  &.label {
    text-align: right;
  }
}
.label {
  font-size: 1.2em;
  font-weight: bold;
  vertical-align: top;
}
.large-value {
  font-size: 1.3em;
}
.error {
  text-transform: capitalize;
  background-color: #fff1f0;
}
.ok {
  text-transform: uppercase;
  background-color: #f6ffed;
}

#select-country {
  width: 270px;
  margin: 30px 5px 30px;
}

#checkbox-explanation {
  margin-top: 15px;
  font-size: 0.9em;
}
</style>
